/**
 * Cliente REST. Duas responsabilidades que não se vêem na assinatura:
 *
 *  1. O access token vive só em memória — nunca em armazenamento persistente,
 *     onde qualquer XSS o leria. O refresh está num cookie HttpOnly que o JS
 *     não alcança (browser) ou no cofre do sistema (app instalada); é isso
 *     que permite recuperar a sessão ao recarregar ou reabrir.
 *
 *  2. Um 401 dispara UM refresh e repete o pedido. Vários pedidos em
 *     paralelo a receber 401 partilham a mesma promessa de refresh — sem
 *     isso, cada um rodava o token e os restantes ficavam com tokens já
 *     usados, que o servidor trata como roubo e revoga a família toda.
 */

import { isNativeClient, sdk } from './config.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message?: string,
    readonly details?: unknown,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

/**
 * Absoluto, não relativo.
 *
 * Na web a origem é a própria página e isto dá exactamente o `/api/v1` de
 * sempre. Numa app instalada a página vem do bundle local, e um caminho
 * relativo nunca sairia da máquina — ver `config.ts`.
 *
 * Uma função e não uma constante: o SDK é configurado no arranque da app, e
 * uma constante de módulo seria avaliada na importação, que acontece antes.
 */
const base = (): string => `${sdk().origin}/api/v1`;

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<(authed: boolean) => void>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const l of listeners) l(token !== null);
}

export function onAuthChange(fn: (authed: boolean) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

async function parseError(res: Response): Promise<ApiError> {
  let code = `http_${res.status}`;
  let message: string | undefined;
  let details: unknown;
  try {
    const body = (await res.json()) as {
      error?: string;
      message?: string;
      details?: unknown;
      issues?: { path: string; message: string }[];
    };
    code = body.error ?? code;
    message = body.message ?? body.issues?.map((i) => `${i.path}: ${i.message}`).join(', ');
    details = body.details;
  } catch {
    /* resposta sem corpo JSON */
  }
  return new ApiError(res.status, code, message, details);
}

/**
 * Headers que distinguem um cliente nativo de um browser.
 *
 * Com isto a API devolve o refresh token no corpo em vez de um cookie — ver
 * `apps/api/src/http/auth.ts`. Sem isto, a app desktop pedia login cada vez
 * que arrancasse.
 */
const nativeHeaders = (): Record<string, string> =>
  isNativeClient() ? { 'x-tasca-native': '1' } : {};

/**
 * Guarda a sessão onde este cliente a sabe guardar.
 *
 * No browser não há nada a fazer: o cookie HttpOnly já foi posto pela
 * resposta e o JS nem o consegue ler. No desktop o token vem no corpo e vai
 * para o keychain do SO.
 */
async function keepSession(body: { refreshToken?: string }): Promise<void> {
  const secrets = sdk().secrets;
  if (!secrets || !body.refreshToken) return;
  await secrets.set(body.refreshToken).catch(() => {
    /* cofre indisponível: a sessão dura até fechar a app */
  });
}

/** Uma só tentativa de refresh partilhada por todos os pedidos pendentes. */
async function refreshOnce(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        // Numa app instalada o token vem do cofre e vai no corpo; no browser
        // vem do cookie e o corpo é vazio.
        const secrets = sdk().secrets;
        const stored = secrets ? await secrets.get().catch(() => null) : null;
        if (secrets && !stored) {
          setAccessToken(null);
          return false;
        }

        const res = await fetch(`${base()}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...nativeHeaders(),
            ...(stored ? { 'content-type': 'application/json' } : {}),
          },
          ...(stored ? { body: JSON.stringify({ refreshToken: stored }) } : {}),
        });
        if (!res.ok) {
          // Um refresh recusado significa sessão morta: apagar o token do
          // cofre evita um ciclo de tentativas com um segredo inútil.
          if (secrets) await secrets.clear().catch(() => {});
          setAccessToken(null);
          return false;
        }
        const body = (await res.json()) as { accessToken: string; refreshToken?: string };
        await keepSession(body);
        setAccessToken(body.accessToken);
        return true;
      } catch {
        setAccessToken(null);
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /**
   * Corpo multipart. Vai tal e qual: o `content-type` tem de ser escrito pelo
   * browser, que é quem sabe a fronteira (`boundary`) do formulário — pô-lo à
   * mão parte o upload de uma forma que só se vê no servidor.
   */
  form?: FormData;
  /** Interno: impede recursão infinita no retry pós-refresh. */
  retried?: boolean;
  signal?: AbortSignal;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...nativeHeaders() };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (accessToken) headers['authorization'] = `Bearer ${accessToken}`;

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
  };
  if (opts.form !== undefined) init.body = opts.form;
  else if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.signal) init.signal = opts.signal;

  const res = await fetch(`${base()}${path}`, init);

  if (res.status === 401 && !opts.retried && !path.startsWith('/auth/')) {
    if (await refreshOnce()) return request<T>(path, { ...opts, retried: true });
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(p: string, signal?: AbortSignal) =>
    request<T>(p, signal ? { signal } : {}),
  post: <T>(p: string, body?: unknown) => request<T>(p, { method: 'POST', body }),
  patch: <T>(p: string, body?: unknown) => request<T>(p, { method: 'PATCH', body }),
  put: <T>(p: string, body?: unknown) => request<T>(p, { method: 'PUT', body }),
  del: <T>(p: string) => request<T>(p, { method: 'DELETE' }),
  upload: <T>(p: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<T>(p, { method: 'POST', form });
  },
};

// ─── Tipos de resposta ───────────────────────────────────────────────────────

export interface PublicUser {
  id: string;
  username: string;
  displayName: string;
  avatarKey: string | null;
  bio: string | null;
  flags: string;
  bot: boolean;
}

export interface PrivateUser extends PublicUser {
  email: string;
  emailVerified: boolean;
  locale: string;
  mfaEnabled: boolean;
}

export interface Channel {
  id: string;
  guildId: string | null;
  type: number;
  name: string | null;
  topic: string | null;
  position: number;
  parentId: string | null;
  rateLimitPerUser: number;
  lastMessageId: string | null;
  permissions?: string;
}

export interface Role {
  id: string;
  guildId: string;
  name: string;
  color: number;
  position: number;
  permissions: string;
  hoist: boolean;
  mentionable: boolean;
}

export interface Guild {
  id: string;
  name: string;
  iconKey: string | null;
  bannerKey: string | null;
  ownerId: string;
  systemChannelId: string | null;
  memberCount?: number;
  roles?: Role[];
  channels?: Channel[];
  permissions?: string;
}

export interface Message {
  id: string;
  channelId: string;
  guildId: string | null;
  author: PublicUser | null;
  content: string;
  type: number;
  flags: number;
  createdAt: string;
  editedAt: string | null;
  referenceId: string | null;
  mentions: string[];
  mentionEveryone: boolean;
  pinned: boolean;
  attachments: { id: string; filename: string; url: string; contentType: string }[];
  reactions: { emoji: string; count: number; me: boolean }[];
  nonce?: string;
  /** Só no cliente: bolha óptimista ainda não confirmada pelo gateway. */
  pending?: boolean;
  failed?: boolean;
}

export interface Member {
  userId: string;
  guildId: string;
  user: PublicUser;
  nick: string | null;
  roles: string[];
  joinedAt: string;
}

// ─── Chamadas ────────────────────────────────────────────────────────────────

interface SessionResponse {
  user: PrivateUser;
  accessToken: string;
  /** Só para clientes nativos — ver `keepSession`. */
  refreshToken?: string;
}

export const auth = {
  async register(body: { username: string; email: string; password: string }) {
    const result = await api.post<SessionResponse>('/auth/register', body);
    await keepSession(result);
    return result;
  },

  async login(body: { identifier: string; password: string }) {
    const result = await api.post<SessionResponse>('/auth/login', body);
    await keepSession(result);
    return result;
  },

  /**
   * Termina a sessão dos dois lados.
   *
   * No desktop o servidor não tem cookie para identificar a sessão, logo o
   * token tem de ir no corpo — senão o logout só esquecia o token
   * localmente e a sessão ficava viva no servidor até expirar. E o keychain
   * é limpo mesmo que o pedido falhe: ficar com um token que já não serve é
   * pior que ficar sem nenhum.
   */
  async logout() {
    const secrets = sdk().secrets;
    const stored = secrets ? await secrets.get().catch(() => null) : null;
    try {
      await api.post<void>('/auth/logout', stored ? { refreshToken: stored } : undefined);
    } finally {
      if (secrets) await secrets.clear().catch(() => {});
    }
  },

  /** Tenta recuperar a sessão: cookie no browser, keychain no desktop. */
  restore: () => refreshOnce(),
};

export const rest = {
  myGuilds: () => api.get<{ guilds: Guild[] }>('/users/@me/guilds'),
  guild: (id: string) => api.get<Guild>(`/guilds/${id}`),
  createGuild: (name: string) => api.post<Guild>('/guilds', { name }),
  createChannel: (
    guildId: string,
    body: { name: string; type?: number; parentId?: string | null },
  ) => api.post<Channel>(`/guilds/${guildId}/channels`, body),
  members: (guildId: string) => api.get<{ members: Member[] }>(`/guilds/${guildId}/members`),
  history: (channelId: string, before?: string) =>
    api.get<{ messages: Message[] }>(
      `/channels/${channelId}/messages?limit=50${before ? `&before=${before}` : ''}`,
    ),
  send: (channelId: string, body: { content: string; nonce: string; referenceId?: string }) =>
    api.post<{ id: string; nonce: string | null }>(`/channels/${channelId}/messages`, body),
  editMessage: (channelId: string, id: string, content: string) =>
    api.patch<Message>(`/channels/${channelId}/messages/${id}`, { content }),
  deleteMessage: (channelId: string, id: string) =>
    api.del<void>(`/channels/${channelId}/messages/${id}`),
  react: (channelId: string, id: string, emoji: string) =>
    api.put<void>(`/channels/${channelId}/messages/${id}/reactions/${encodeURIComponent(emoji)}`),
  unreact: (channelId: string, id: string, emoji: string) =>
    api.del<void>(`/channels/${channelId}/messages/${id}/reactions/${encodeURIComponent(emoji)}`),
  typing: (channelId: string) => api.post<void>(`/channels/${channelId}/typing`),
  ack: (channelId: string, messageId: string) =>
    api.post<void>(`/channels/${channelId}/ack`, { messageId }),
  createInvite: (channelId: string) =>
    api.post<{ code: string }>(`/channels/${channelId}/invites`, {}),
  acceptInvite: (code: string) => api.post<Guild>(`/invites/${code}`),

  // ─── Gestão ───────────────────────────────────────────────────
  //
  // Quem pode fazer cada uma destas é decidido no servidor, sempre. O cliente
  // esconde o que não pode fazer para não oferecer botões mortos — não é isso
  // que impede ninguém de nada.
  updateChannel: (
    channelId: string,
    patch: { name?: string; topic?: string | null; rateLimitPerUser?: number },
  ) => api.patch<Channel>(`/channels/${channelId}`, patch),
  deleteChannel: (channelId: string) => api.del<void>(`/channels/${channelId}`),
  /** A ordem inteira de uma vez — ver a nota em reorderChannelsSchema. */
  reorderChannels: (
    guildId: string,
    items: { id: string; position: number; parentId?: string | null }[],
  ) => api.patch<{ channels: Channel[] }>(`/guilds/${guildId}/channels`, { channels: items }),
  updateMember: (guildId: string, userId: string, patch: { nick?: string | null }) =>
    api.patch<Member>(`/guilds/${guildId}/members/${userId}`, patch),
  /** Expulsar. Com o próprio id, é sair do guild. */
  kickMember: (guildId: string, userId: string) =>
    api.del<void>(`/guilds/${guildId}/members/${userId}`),
  banMember: (guildId: string, userId: string, reason?: string) =>
    api.put<void>(`/guilds/${guildId}/bans/${userId}`, reason ? { reason } : {}),

  // ─── Servidor ──────────────────────────────────────────────────────────────
  updateGuild: (guildId: string, patch: { name?: string; systemChannelId?: string | null }) =>
    api.patch<Guild>(`/guilds/${guildId}`, patch),
  /** O ficheiro, nunca a chave — ver a nota na rota. */
  uploadGuildIcon: (guildId: string, file: File) =>
    api.upload<Guild>(`/guilds/${guildId}/icon`, file),
  clearGuildIcon: (guildId: string) => api.del<Guild>(`/guilds/${guildId}/icon`),
  uploadGuildBanner: (guildId: string, file: File) =>
    api.upload<Guild>(`/guilds/${guildId}/banner`, file),
  clearGuildBanner: (guildId: string) => api.del<Guild>(`/guilds/${guildId}/banner`),

  // ─── Papéis ────────────────────────────────────────────────────────────────
  createRole: (guildId: string, body: { name: string; color?: number; permissions?: string }) =>
    api.post<Role>(`/guilds/${guildId}/roles`, body),
  updateRole: (
    guildId: string,
    roleId: string,
    patch: {
      name?: string;
      color?: number;
      permissions?: string;
      hoist?: boolean;
      mentionable?: boolean;
    },
  ) => api.patch<Role>(`/guilds/${guildId}/roles/${roleId}`, patch),
  deleteRole: (guildId: string, roleId: string) =>
    api.del<void>(`/guilds/${guildId}/roles/${roleId}`),
  /** Dar e tirar papéis a um membro, numa só chamada. */
  setMemberRoles: (
    guildId: string,
    userId: string,
    body: { add?: string[]; remove?: string[] },
  ) => api.patch<Member>(`/guilds/${guildId}/members/${userId}/roles`, body),

  // ─── Voz ───────────────────────────────────────────────────────────────────
  voiceStates: (channelId: string) =>
    api.get<{ channelId: string; states: VoiceStateDto[] }>(
      `/channels/${channelId}/voice-states`,
    ),
  guildVoiceStates: (guildId: string) =>
    api.get<{ guildId: string; states: VoiceStateDto[] }>(`/guilds/${guildId}/voice-states`),
  /** Moderação: serverMute, serverDeaf, ou mover (`channelId`). */
  moderateVoice: (
    guildId: string,
    userId: string,
    patch: { serverMute?: boolean; serverDeaf?: boolean; channelId?: string | null },
  ) => api.patch<VoiceModerationResult>(`/guilds/${guildId}/voice-states/${userId}`, patch),
  disconnectVoice: (guildId: string, userId: string) =>
    api.del<void>(`/guilds/${guildId}/voice-states/${userId}`),
};

export interface VoiceStateDto {
  userId: string;
  channelId: string | null;
  guildId: string | null;
  selfMute: boolean;
  selfDeaf: boolean;
  selfVideo: boolean;
  selfStream: boolean;
  serverMute: boolean;
  serverDeaf: boolean;
}

export interface VoiceModerationResult {
  userId: string;
  guildId: string;
  channelId: string | null;
  serverMute: boolean;
  serverDeaf: boolean;
}
